// In-app support / feedback plumbing.
//
// Two ways for a user to reach us, both surfaced in Settings → Help:
//   (1) supportMailto() — opens the user's mail app to support@classboon.com,
//       prefilled with a subject + their account context so a reply has what it
//       needs. Works with zero backend.
//   (2) submitSupportMessage() — writes the message straight into Firestore
//       (`supportMessages`), so a teacher who can't/won't use email still gets
//       through. The super-admin console reads these (see AdminPanel).
//
// The `supportMessages` doc MUST carry `uid == request.auth.uid` — that's the
// only thing the create rule allows, so a user can only ever file their own.

import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { firestore } from "./FirebaseConfig";

export const SUPPORT_EMAIL = "support@classboon.com";

// Build a mailto: link prefilled with a subject + a context block so replies land
// with the who/where already filled in. Everything is URL-encoded.
export function supportMailto({ name, email, schoolName, role, category } = {}) {
  const subject = category
    ? `classBoon support — ${category}`
    : "classBoon support request";
  const lines = [
    "",
    "",
    "— — — — —",
    "Please describe what you were trying to do and what happened.",
    "",
    `Name:   ${name || "(your name)"}`,
    `School: ${schoolName || "(your school)"}`,
    `Role:   ${role || "teacher"}`,
    email ? `Email:  ${email}` : null,
  ].filter((l) => l !== null);
  const body = lines.join("\n");
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

// Persist an in-app message. Best-effort context (school, role, page) is attached
// so we can triage without a round-trip. Throws on failure so the caller can show
// an error and fall back to the mailto link.
export async function submitSupportMessage({
  user,
  role,
  schoolId,
  schoolName,
  category,
  message,
}) {
  if (!user?.uid) throw new Error("Not signed in.");
  const text = (message || "").trim();
  if (!text) throw new Error("Message is empty.");

  await addDoc(collection(firestore, "supportMessages"), {
    uid: user.uid,
    name: user.displayName || "",
    email: user.email || "",
    role: role || "teacher",
    schoolId: schoolId || null,
    schoolName: schoolName || "",
    category: category || "General",
    message: text,
    page: typeof window !== "undefined" ? window.location.pathname : "",
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    status: "new",
    createdAt: serverTimestamp(),
  });
}
